/**
 * authErrorSuppression — decide whether a data-load error toast should be hidden.
 *
 * WHY (mobile logout noise, 2026-06-28):
 * When a session expires, in-flight data fetches (chat history, Smart Flow,
 * etc.) 401 and their .catch() handlers fire — often *after* the auth
 * interceptor has already triggered a forced logout + redirect to /login.
 * The user then lands on the login page with stray "Failed to load …" toasts
 * stacked on top, which is confusing noise: they aren't being told anything
 * actionable, they're just seeing the corpses of requests that died with the
 * session.
 *
 * Any error handler that would show a toast should first consult
 * `shouldSuppressAuthErrorToast()` and stay silent when a logout is underway
 * or the user is no longer authenticated.
 *
 * Reads auth state via `window.store` (set in store/index.ts) rather than
 * importing the store directly — store/authSlice.ts already imports
 * logoutCoordinator, so importing the store here would create a cycle.
 */

import { LogoutCoordinator } from './logoutCoordinator';

export function shouldSuppressAuthErrorToast(): boolean {
  // A forced logout is in flight — the user is already being sent to login.
  if (LogoutCoordinator.active) {
    return true;
  }

  // The user is no longer authenticated (session already torn down).
  try {
    const auth = (window as any).store?.getState?.()?.auth;
    if (auth && auth.isAuthenticated === false) {
      return true;
    }
  } catch {
    // If the store isn't reachable, fall through and let the toast show.
  }

  return false;
}
